/**
 * ImpersonationBanner — global "you are viewing another org's data" bar.
 *
 * Mounted once in App.tsx, above the router outlet, inside
 * ImpersonationProvider, so it shows on every page whenever an admin has
 * an active impersonation session (`useImpersonation().orgId` set). Renders
 * nothing otherwise — this component adds zero DOM when not impersonating.
 *
 * High-contrast + `sticky top-0` so it stays visible while scrolling and is
 * hard to miss, since every request made while it's showing carries the
 * `X-Impersonate-Org` header (see impersonationHeaders.ts) and therefore
 * sees the impersonated org's data, not the admin's own.
 */
import React from 'react';
import { useImpersonation } from '../contexts/ImpersonationContext';

const ImpersonationBanner: React.FC = () => {
  const { orgId, orgName, exit } = useImpersonation();

  if (orgId === null) {
    return null;
  }

  return (
    <div
      role="alert"
      className="sticky top-0 z-[1000] flex items-center justify-center gap-3 bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 shadow-md"
    >
      <span>
        ⚠ Viewing as {orgName} —
      </span>
      <button
        type="button"
        onClick={exit}
        className="rounded bg-slate-900 px-2.5 py-0.5 text-xs font-semibold text-amber-300 hover:bg-slate-700 transition-colors"
      >
        Exit
      </button>
    </div>
  );
};

export default ImpersonationBanner;
